import {
  Box,
  Button,
  Flex,
  LinkBox,
  LinkOverlay,
  Spinner,
  Stack,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import {
  NeedOverviewFigure,
  useGetNeedOverviewQuery,
  type NeedDepartmentBarDto,
  type NeedFigureDto,
  type NeedOverviewDto,
} from "../../autogen/bff-api";
import { ListLoadError } from "../../common/ListLoadError";
import { useApiError } from "../../common/errors/useApiError";
import { displayDate } from "../../common/formatting/displayDate";
import { displaySum } from "../../common/formatting/displaySum";
import { formatNumber } from "../../common/formatting/formatNumber";
import { BUCKET_META } from "../../common/layout/statusBuckets";
import { useNavLabel } from "../../common/layout/useNavLabel";
import { useNeedStatusLabel } from "../../common/needs/useNeedStatusLabel";
import { urls } from "../../urls";
import { useNeedPriorityLabel } from "../behov/useNeedPriorityLabel";
import { PHASE_BUCKET } from "../projects/view-single/ProcurementSummary";
import { QUEUE_LINK } from "../home/panels/NeedsWaitingPanel";
import { MutedCell, PanelRow, TitleCell } from "../home/panels/PanelTable";
import { OverviewBar } from "./OverviewBar";
import { OverviewFigureGrid, OverviewFigureTile } from "./OverviewFigureTile";
import { IncompletePeriod, OverviewPeriodChoice, useOverviewPeriod } from "./OverviewPeriodChoice";
import {
  NEED_FIGURE_ORDER,
  needFigureDefinition,
  needFigureLabel,
  needListLink,
  plannedQuarterLink,
  needScopeText,
  procurementsFromNeedsLink,
  reportedNeedsLink,
} from "./needFigures";
import type { PeriodRange } from "./procurementFigures";

export const NeedsOverview = () => {
  const periodState = useOverviewPeriod();
  const { range } = periodState;
  const { data, isLoading, isFetching, error, refetch } = useGetNeedOverviewQuery(
    range ? { from: range.from, to: range.to } : skipToken,
  );
  const { message } = useApiError();

  return (
    <Stack spacing="5">
      <Flex gap="3" wrap="wrap" align="center">
        <OverviewPeriodChoice state={periodState} />
        {isFetching && !isLoading && <Spinner size="sm" color="smPrimary" />}
      </Flex>
      {!range ? (
        <IncompletePeriod />
      ) : isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !data ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <>
          <Figures overview={data} range={range} />
          <StatusBars overview={data} range={range} />
          <DepartmentBars overview={data} range={range} />
          <PhaseBars overview={data} range={range} />
          <PlannedPerQuarter overview={data} />
          <Queue overview={data} />
        </>
      )}
    </Stack>
  );
};

const Figures = ({ overview, range }: { overview: NeedOverviewDto; range: PeriodRange }) => {
  const { t } = useTranslation();
  const byFigure = new Map(overview.figures.map((figure) => [figure.figure, figure]));
  const unknownInSum = byFigure.get(NeedOverviewFigure.BecameProcurement)?.valueUnknown ?? 0;
  return (
    <Stack spacing="4">
      <OverviewFigureGrid columns={{ base: 1, sm: 2, lg: 3 }}>
        {NEED_FIGURE_ORDER.map((key) => {
          const figure = byFigure.get(key);
          return figure ? (
            <OverviewFigureTile
              key={key}
              label={needFigureLabel(t, key)}
              shown={shownValue(t, figure)}
              below={<Below figure={figure} />}
              definition={needFigureDefinition(t, key)}
              details={<FigureDetails figure={figure} />}
              link={needListLink(key, range)}
              testId={key}
            />
          ) : null;
        })}
      </OverviewFigureGrid>
      <Stack spacing="1" fontSize="sm" color="smMuted">
        <Text>{t("Based on what you have access to: {{scope}}", { scope: needScopeText(t, overview.scope) })}</Text>
        {unknownInSum > 0 && (
          <Text data-testid="value-unknown-note">
            {t("{{count}} needs have «Don't know» as value and are not included in the sum.", { count: unknownInSum })}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

const shownValue = (t: TFunction, figure: NeedFigureDto): string => {
  if (figure.figure !== NeedOverviewFigure.MedianQueueDays) return formatNumber(figure.needs);
  return figure.days == null ? "–" : t("{{count}} days", { count: Math.round(figure.days) });
};

const Below = ({ figure }: { figure: NeedFigureDto }) => {
  const { t } = useTranslation();
  const line = belowText(t, figure);
  return line ? (
    <Text fontSize="sm" color="smMuted" mt="1">
      {line}
    </Text>
  ) : null;
};

const belowText = (t: TFunction, figure: NeedFigureDto): string | null => {
  switch (figure.figure) {
    case NeedOverviewFigure.Reported:
      return t("Same period last year: {{count}}", { count: figure.samePeriodLastYear ?? 0 });
    case NeedOverviewFigure.InQueue:
      return figure.days == null ? null : t("The oldest has waited {{count}} days", { count: figure.days });
    case NeedOverviewFigure.DeliverySoon:
      return figure.deliveryPassed ? t("{{count}} have passed the date", { count: figure.deliveryPassed }) : null;
    case NeedOverviewFigure.BecameProcurement:
      return `${t("Estimated value of the needs")}: ${nok(figure.estimatedValue)}`;
    default:
      return null;
  }
};

const FigureDetails = ({ figure }: { figure: NeedFigureDto }) => {
  const { t } = useTranslation();
  const statusLabel = useNeedStatusLabel();
  return (
    <Stack spacing="1">
      {figure.figure === NeedOverviewFigure.MedianQueueDays && (
        <Text>{t("Based on {{count}} needs taken or rejected.", { count: figure.needs })}</Text>
      )}
      {figure.byStatus.map((count) => (
        <Text key={count.status}>
          {statusLabel(count.status)}: {formatNumber(count.needs)}
        </Text>
      ))}
      {figure.needs > 0 && (
        <Text>
          {t("Estimated value of the needs")}: {nok(figure.estimatedValue)}
        </Text>
      )}
      {figure.valueUnknown > 0 && (
        <Text>
          {t("{{count}} needs have «Don't know» as value and are not included in the sum.", { count: figure.valueUnknown })}
        </Text>
      )}
    </Stack>
  );
};

const StatusBars = ({ overview, range }: { overview: NeedOverviewDto; range: PeriodRange }) => {
  const { t } = useTranslation();
  const statusLabel = useNeedStatusLabel();
  return (
    <NeedBarCard
      title={t("Needs by status")}
      subtitle={t("Needs first sent in the period, by where they are now.")}
      bars={overview.byStatus.map((bar) => ({
        key: bar.status,
        label: statusLabel(bar.status),
        tally: bar,
        extra:
          bar.medianDaysInStatus == null
            ? []
            : [t("Median {{count}} days in this status", { count: Math.round(bar.medianDaysInStatus) })],
        link: reportedNeedsLink(range, { status: bar.status }),
      }))}
      testIdPrefix="status"
    />
  );
};

const DepartmentBars = ({ overview, range }: { overview: NeedOverviewDto; range: PeriodRange }) => {
  const { t } = useTranslation();
  return (
    <NeedBarCard
      title={t("Per department")}
      subtitle={t("Needs first sent in the period, by the department they were sent from.")}
      bars={overview.byDepartment.map((bar) => ({
        key: bar.departmentId ?? "none",
        label: departmentLabel(t, bar),
        tally: bar,
        extra: [],
        link: bar.departmentId ? reportedNeedsLink(range, { department: bar.departmentId }) : null,
      }))}
      testIdPrefix="department"
    />
  );
};

const PhaseBars = ({ overview, range }: { overview: NeedOverviewDto; range: PeriodRange }) => {
  const { t } = useTranslation();
  const navLabel = useNavLabel();
  const anyFromNeeds = overview.byProcurementPhase.some((bar) => bar.needs > 0);
  return (
    <NeedBarCard
      title={t("From need to contract, for needs first sent in the period")}
      subtitle={String(
        t(
          "Needs first sent in the period that became a procurement or mini-competition, by the phase the procurement is in now."
        )
      )}
      empty={String(t("No need sent in this period has become a procurement or mini-competition."))}
      bars={
        anyFromNeeds
          ? overview.byProcurementPhase.map((bar) => ({
              key: bar.phase,
              label: navLabel(BUCKET_META[PHASE_BUCKET[bar.phase]].labelKey),
              tally: bar,
              extra: [],
              link: bar.needs > 0 ? procurementsFromNeedsLink(bar.phase, range) : null,
            }))
          : []
      }
      testIdPrefix="phase"
    />
  );
};

const PlannedPerQuarter = ({ overview }: { overview: NeedOverviewDto }) => {
  const { t } = useTranslation();
  const largest = Math.max(1, ...overview.plannedPerQuarter.map((bar) => bar.estimatedValue));
  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Box>
        <Text as="h2" fontWeight="semibold" color="smText">
          {t("Planned per quarter")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("Estimated value of planned needs, by the quarter of their planned start. Not limited by the period above.")}
        </Text>
      </Box>
      <Stack spacing="1">
        {overview.plannedPerQuarter.map((bar) => (
          <OverviewBar
            key={`${bar.year}-${bar.quarter}`}
            label={t("{{quarter}}. quarter {{year}}", { quarter: bar.quarter, year: bar.year })}
            width={(bar.estimatedValue / largest) * 100}
            shown={nok(bar.estimatedValue)}
            details={[
              t("{{count}} needs", { count: bar.needs }),
              t("«Don't know» as value: {{count}}", { count: bar.valueUnknown }),
            ]}
            link={plannedQuarterLink(bar)}
            linkLabel={String(t("Open the procurement plan"))}
            testId={`planned-${bar.year}-${bar.quarter}`}
          />
        ))}
      </Stack>
    </Stack>
  );
};

const departmentLabel = (t: TFunction, bar: NeedDepartmentBarDto): string => {
  if (!bar.departmentName) return t("No department");
  return bar.retired ? `${bar.departmentName} (${t("Retired")})` : bar.departmentName;
};

const NeedBarCard = ({
  title,
  subtitle,
  empty,
  bars,
  testIdPrefix,
}: {
  title: string;
  subtitle: string;
  empty?: string;
  bars: {
    key: string;
    label: string;
    tally: { needs: number; estimatedValue: number; valueUnknown: number };
    extra: string[];
    link: string | null;
  }[];
  testIdPrefix: string;
}) => {
  const { t } = useTranslation();
  const largest = Math.max(1, ...bars.map((bar) => bar.tally.needs));
  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Box>
        <Text as="h2" fontWeight="semibold" color="smText">
          {title}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {subtitle}
        </Text>
      </Box>
      {bars.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {empty ?? t("No need was sent in this period.")}
        </Text>
      ) : (
        <Stack spacing="1">
          {bars.map((bar) => (
            <OverviewBar
              key={bar.key}
              label={bar.label}
              width={(bar.tally.needs / largest) * 100}
              shown={formatNumber(bar.tally.needs)}
              details={[
                t("{{count}} needs", { count: bar.tally.needs }),
                `${t("Estimated value of the needs")}: ${nok(bar.tally.estimatedValue)}`,
                t("«Don't know» as value: {{count}}", { count: bar.tally.valueUnknown }),
                ...bar.extra,
              ]}
              link={bar.link}
              testId={`${testIdPrefix}-${bar.key}`}
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const Queue = ({ overview }: { overview: NeedOverviewDto }) => {
  const { t } = useTranslation();
  const stacked = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false;
  const priorityLabel = useNeedPriorityLabel();
  const rows = overview.queue.map((need) => ({
    need,
    to: urls.app.needs.view.go(need.id),
    title: need.title?.trim() || t("Untitled"),
    value: need.value == null ? t("Don't know") : nok(need.value),
    priority: need.priority ? priorityLabel(need.priority) : "–",
    sent: need.sentAt ? displayDate(need.sentAt) : "–",
    delivery: need.deliveryDate ? displayDate(need.deliveryDate) : "–",
  }));
  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Text as="h2" fontWeight="semibold" color="smText">
        {t("The queue")}
      </Text>
      {rows.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {t("No needs are waiting.")}
        </Text>
      ) : stacked ? (
        <Stack spacing="2">
          {rows.map((row) => (
            <LinkBox
              key={row.need.id}
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="lg"
              px="4"
              py="3"
              _hover={{ borderColor: "smPrimary" }}
              data-testid="queue-row"
            >
              <LinkOverlay as={RouterLink} to={row.to} fontWeight="medium" color="smText" overflowWrap="anywhere">
                {row.title}
              </LinkOverlay>
              <Text fontSize="sm" color="smMuted" mt="1">
                {[row.need.departmentName, row.value, row.priority].filter(Boolean).join(" · ")}
              </Text>
              <Text fontSize="sm" color="smMuted">
                {t("{{count}} days in the queue", { count: row.need.daysInQueue })} ·{" "}
                {t("Desired delivery")}: {row.delivery}
              </Text>
            </LinkBox>
          ))}
        </Stack>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t("Title")}</Th>
                <Th>{t("Department")}</Th>
                <Th isNumeric>{t("Estimated value of the needs")}</Th>
                <Th>{t("Priority")}</Th>
                <Th>{t("Sent")}</Th>
                <Th isNumeric>{t("Days in the queue")}</Th>
                <Th>{t("Desired delivery")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <PanelRow key={row.need.id} to={row.to}>
                  <TitleCell title={row.title} clampLines={2} />
                  <MutedCell whiteSpace="normal">{row.need.departmentName ?? "–"}</MutedCell>
                  <MutedCell whiteSpace="nowrap" isNumeric>
                    {row.value}
                  </MutedCell>
                  <MutedCell whiteSpace="nowrap">{row.priority}</MutedCell>
                  <MutedCell whiteSpace="nowrap">{row.sent}</MutedCell>
                  <MutedCell whiteSpace="nowrap" isNumeric>
                    {formatNumber(row.need.daysInQueue)}
                  </MutedCell>
                  <MutedCell whiteSpace="nowrap">{row.delivery}</MutedCell>
                </PanelRow>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
      {overview.queueCount > rows.length && (
        <Button
          as={RouterLink}
          to={QUEUE_LINK}
          variant="link"
          size="sm"
          colorScheme="teal"
          alignSelf="flex-start"
        >
          {t("See all {{count}} in the queue", { count: overview.queueCount })}
        </Button>
      )}
    </Stack>
  );
};

const nok = (value: number): string => displaySum({ value, currency: "NOK" });
